import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Shuffle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LanguageFlags } from "./LanguageFlags";
import { useAuth } from "@/hooks/useAuth";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

interface AnagramWord {
  word: string;
  language: number;
}

interface AnagramResult {
  words: AnagramWord[];
  wordCount: number;
}

const ITEMS_PER_PAGE = 50;

export const AnagramSearch = () => {
  const { isAdmin } = useAuth();
  const [letters, setLetters] = useState("");
  const [results, setResults] = useState<AnagramResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Sort letters alphabetically for comparison
  const sortLetters = (str: string): string => {
    return str.toUpperCase().split("").sort().join("");
  };

  // Check if word letters are a subset of available letters
  const canFormWord = (wordLetters: string, availableLetters: string): boolean => {
    const available = availableLetters.split("");
    for (const letter of wordLetters.split("")) {
      const index = available.indexOf(letter);
      if (index === -1) return false;
      available.splice(index, 1);
    }
    return true;
  };

  // Find multi-word anagrams recursively with limits to prevent long searches
  const findMultiWordAnagrams = (
    remainingLetters: string,
    currentWords: string[],
    availableWords: { word: string; sortedLetters: string }[],
    results: Set<string>,
    maxWords: number = 4,
    maxResults: number = 1000
  ) => {
    // Stop if we've found enough results
    if (results.size >= maxResults) return;
    
    if (remainingLetters.length === 0 && currentWords.length > 0) {
      // Found a valid combination - sort words to avoid duplicates
      const sortedCombination = [...currentWords].sort().join(" ");
      results.add(sortedCombination);
      return;
    }

    if (currentWords.length >= maxWords) return;
    
    // Skip if remaining letters are too few to form any word
    if (remainingLetters.length < 2) return;

    for (const { word, sortedLetters } of availableWords) {
      // Stop early if we have enough results
      if (results.size >= maxResults) return;
      
      if (sortedLetters.length > remainingLetters.length) continue;
      if (!canFormWord(sortedLetters, remainingLetters)) continue;

      // Calculate remaining letters after using this word
      const remaining = remainingLetters.split("");
      for (const letter of sortedLetters.split("")) {
        const index = remaining.indexOf(letter);
        remaining.splice(index, 1);
      }

      findMultiWordAnagrams(
        remaining.join(""),
        [...currentWords, word],
        availableWords,
        results,
        maxWords,
        maxResults
      );
    }
  };

  const handleSearch = async () => {
    if (!letters.replace(/\s/g, "").trim()) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    setCurrentPage(1);

    try {
      const cleanedLetters = letters.replace(/\s/g, "").trim();
      const sortedInput = sortLetters(cleanedLetters);

      // Use server-side function to filter candidates
      const { data, error: fetchError } = await supabase
        .rpc("find_anagram_candidates", { 
          input_letters: cleanedLetters,
          max_length: cleanedLetters.length 
        });

      if (fetchError) throw fetchError;

      // Create a map of word -> language for lookup
      const wordLanguageMap = new Map<string, number>();
      (data || []).forEach(row => {
        const capitalizedWord = row.word.charAt(0).toUpperCase() + row.word.slice(1).toLowerCase();
        wordLanguageMap.set(capitalizedWord, row.language ?? 1);
      });

      // Words are already filtered by the database function, just format them
      // Filter out very short words (1 letter) to reduce combinations
      const validWords = (data || [])
        .filter((row) => row.word.length >= 2)
        .map((row) => ({
          word: row.word.charAt(0).toUpperCase() + row.word.slice(1).toLowerCase(),
          sortedLetters: sortLetters(row.word),
          language: row.language ?? 1,
        }))
        // Sort by word length descending to find better matches first
        .sort((a, b) => b.sortedLetters.length - a.sortedLetters.length);

      // Limit candidate words to prevent exponential complexity
      const maxCandidates = 150;
      const limitedWords = validWords.slice(0, maxCandidates);

      // Find single-word anagrams (exact matches) - use full list for single words
      const singleWordAnagrams: AnagramResult[] = validWords
        .filter((w) => w.sortedLetters === sortedInput && w.word.toUpperCase() !== cleanedLetters.toUpperCase())
        .map((w) => ({ words: [{ word: w.word, language: w.language }], wordCount: 1 }));

      // Find multi-word anagrams with limited candidates
      const multiWordResults = new Set<string>();
      const maxResults = 1000;
      findMultiWordAnagrams(sortedInput, [], limitedWords, multiWordResults, 2, maxResults);

      // Convert to result format and filter out single words
      const multiWordAnagrams: AnagramResult[] = Array.from(multiWordResults)
        .map((combo) => {
          const wordStrings = combo.split(" ");
          const words: AnagramWord[] = wordStrings.map(w => ({
            word: w,
            language: wordLanguageMap.get(w) ?? 1
          }));
          return { words, wordCount: words.length };
        })
        .filter((r) => r.wordCount > 1);

      // Combine and sort results
      const allResults = [...singleWordAnagrams, ...multiWordAnagrams]
        .sort((a, b) => {
          if (a.wordCount !== b.wordCount) return a.wordCount - b.wordCount;
          return a.words.map(w => w.word).join(" ").localeCompare(b.words.map(w => w.word).join(" "), "sv");
        });

      setResults(allResults);

      // Log the search - skip if admin
      if (!isAdmin) {
        await supabase.from("search_logs").insert({
          search_term: cleanedLetters,
          search_type: "anagram",
          results_count: allResults.length,
        });
      }
    } catch (err) {
      console.error("Anagram search error:", err);
      setError("Ett fel uppstod vid sökningen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Group results by word count
  const groupedResults = results.reduce((acc, result) => {
    const key = result.wordCount;
    if (!acc[key]) acc[key] = [];
    acc[key].push(result);
    return acc;
  }, {} as Record<number, AnagramResult[]>);

  // Pagination logic
  const totalPages = Math.ceil(results.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  // Get paginated results while maintaining grouping
  const getPaginatedGroupedResults = () => {
    const flatResults = results.slice(startIndex, endIndex);
    return flatResults.reduce((acc, result) => {
      const key = result.wordCount;
      if (!acc[key]) acc[key] = [];
      acc[key].push(result);
      return acc;
    }, {} as Record<number, AnagramResult[]>);
  };

  const paginatedGroupedResults = getPaginatedGroupedResults();

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis");
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-foreground mb-1">Anagramsökning</h2>
          <p className="text-sm text-muted-foreground">
            Hitta ord och ordkombinationer som kan bildas med exakt dessa bokstäver
          </p>
        </div>

        <div className="flex gap-2">
          <Input
            value={letters}
            onChange={(e) => setLetters(e.target.value.replace(/[^a-zA-ZåäöÅÄÖ\s]/g, ""))}
            onKeyDown={handleKeyDown}
            placeholder="T.ex. TORGET → GRÖT TE, TROGE..."
            className="flex-1 uppercase"
            maxLength={20}
          />
          <Button onClick={handleSearch} disabled={isLoading || !letters.replace(/\s/g, "").trim()}>
            {isLoading ? (
              <Shuffle className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-2 hidden sm:inline">Sök</span>
          </Button>
        </div>

        {letters.replace(/\s/g, "") && (
          <p className="text-sm text-muted-foreground text-center">
            Söker efter anagram med bokstäverna: {sortLetters(letters.replace(/\s/g, ""))}
          </p>
        )}
      </div>

      {error && (
        <div className="text-center text-destructive py-4">{error}</div>
      )}

      {hasSearched && !isLoading && !error && (
        <div className="space-y-6">
          {results.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                Hittade {results.length} anagram
                {totalPages > 1 && ` (visar ${startIndex + 1}-${Math.min(endIndex, results.length)})`}
              </p>
              {Object.entries(paginatedGroupedResults)
                .sort(([a], [b]) => Number(a) - Number(b))
                .map(([wordCount, items]) => (
                  <div key={wordCount} className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground border-b border-border pb-1">
                      {wordCount === "1" ? "Ett ord" : `${wordCount} ord`} ({items.length})
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {items.map((result, index) => (
                        <div
                          key={index}
                          className="bg-muted/50 rounded-lg px-3 py-2 flex items-center justify-center gap-2 flex-wrap"
                        >
                          {result.words.map((w, wordIndex) => (
                            <span key={wordIndex} className="inline-flex items-center gap-1">
                              <span className="font-medium">{w.word}</span>
                              <LanguageFlags language={w.language} className="text-xs" />
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    {getPageNumbers().map((page, index) =>
                      page === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${index}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    )}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Shuffle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Inga anagram hittades för "{letters.toUpperCase()}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};